import "abortcontroller-polyfill/dist/abortcontroller-polyfill-only"; // Polyfill AbortController
import "whatwg-fetch"; // Polyfill fetch
import { v4 as uuid } from "uuid";
import {
    captureException
} from "extensions/sentry";

import {
    updateQueryStringParameters,
    toQueryString
} from "common/utils";
import { emitter } from "common/emitter";
import { store } from "common/store";

import {
    ApiErrorFetchingData,
    AbortError,
    TypeErrorRequest,
    ApiTimeoutFetchingData,
    ErrorNotAuthenticated,
    RequestError
} from "common/helpers/errors";

// eslint-disable-next-line import/no-cycle
import { REFRESH_TOKEN } from "common/actions/authentication";
import { pageTracker } from "common/components/pageTracker";

/**
 * App request method
 * custom options
 * ensureAuth - boolean or function to check if authentication is required for this call
 */
export const request = async function(url, opts = Object.create(null)) {
    const headers = {
        authToken: "X-Paz-Authentication",
        tokenExpiration: "x-paz-auth-expires",
        forcePasswordChangeToken: "x-paz-renewauthentication",
        passExpires: "x-paz-pass-expires",
        request_id: "x-paz-request-id",
        product: "x-paz-product",
        requestedUrl: "X-Paz-Requested-Url",
        groupId: "X-Paz-Request-Group-Id"
    };
    
    const excludedTrackingEndpoints = [
        "user/notification/",
        "/package_reports_notifications",
        "user/export",
        "export/statuses/",
        "user/export/token/",
        "/version",
        "/healthcheck",
        "/configuration/",
        "/auth/renew/",
        "/tracking"
    ];
    
    // Throw error if there is no URL
    if (typeof url === "undefined" || url === null) {
        throw new Error("URL is not specified");
    }
    
    if (typeof url !== "string") {
        throw new TypeError("URL must be a string");
    }
    
    if (opts.success || opts.error) {
        throw new Error("Request component does not longer support success and error handlers. Switch to then/catch instead");
    }
    
    if (typeof opts.data === "string") {
        throw new TypeError("Data cannot be a string");
    }
    
    // Add defaults to options
    opts = {
        method: "GET",
        ensureAuth: true,
        signal: void 0, // AbortController signal
        data: {},
        prefilterUrl: true, // Add API host, replace :merchant_ref, and add bu_ref
        refresh_token: true,
        ...opts,
        headers: {
            ...opts.headers
        }
    };
    
    const original_url = url;
    
    if (opts.prefilterUrl) {
        const state = store.getState();
        url = `${state.config.api_host}${url}`.replace(/:merchant_ref/g, state.common.merchant_ref);
    
        if (state.common.bu_ref) {
            // Add querystring to indicate a specific BU ref
            url = updateQueryStringParameters(url, "bu_ref", state.common.bu_ref);
        }
    }
    
    // Build fetch options
    const requestOptions = {
        method: opts.method,
        headers: opts.headers
    };
    
    if (Object.keys(opts.data).length) {
        if (["POST", "PUT", "PATCH"].includes(opts.method)) {
            requestOptions.body = JSON.stringify(opts.data);
            requestOptions.headers = {
                ...requestOptions.headers,
                "Content-Type": "application/json"
            };
        } else if (opts.method === "GET") {
            url = [url, toQueryString(opts.data)].join(url.includes("?") ? "&" : "?");
        }
    }
    
    if (opts.ensureAuth) {
        requestOptions.headers = {
            Authorization: `JWT ${store.getState().authentication.access_token}`,
            ...requestOptions.headers
        };
    }
    
    const mimic_as_user = store.getState().authentication.mimic_as_user;
    if (mimic_as_user) {
        requestOptions.headers = {
            "as-user": mimic_as_user,
            ...requestOptions.headers
        };
    }
    
    // Set all other headers
    if (!opts.preventPreFlight) {
        requestOptions.headers = {
            [headers.requestedUrl]: `${window.location.pathname}${window.location.hash}`,
            [headers.groupId]: pageTracker.getCurrentRequestGroupID(),
            [headers.request_id]: uuid(),
            [headers.product]: store.getState().common.product,
            ...requestOptions.headers
        };
    }
    
    if (opts.signal) {
        requestOptions.signal = opts.signal;
    }
    
    const requestProps = {
        startTime: Date.now(),
        endTime: null,
        duration: null,
        id: requestOptions.headers[headers.request_id],
        url
    };
    
    const _request = window.fetch(url, requestOptions);
    Object.assign(_request, requestProps);
    
    if (store.getState().config.pageTracking
        && opts.ensureAuth
        && excludedTrackingEndpoints.every((u) => !url.includes(u))) {
        pageTracker.track(_request);
    }
    
    let response;
    let error = false;
    
    try {
        response = await _request;
    } catch (error_) {
        response = {
            status: 0,
            url,
            statusText: error_.toString()
        };
        
        // Here we can catch "TypeError: Failed to fetch" and AbortErrors
        // We need to add other request options to properly log it
        if (error_.name === "AbortError") {
            error = new AbortError(response);
        } else if (error_.name === "TypeError") {
            error = new TypeErrorRequest(response);
            // This is hands down, one of the worst error messages I’ve ever seen: "TypeError: Failed to fetch".
            // This can happen when the API goes down due to some db problems and then error response doesn't include CORS-specific headers.
            // The same can happen when network request is aborted on user's side (router, etc)
        } else {
            error = error_;
        }
    }
    
    requestProps.endTime = Date.now();
    requestProps.duration = requestProps.endTime - requestProps.startTime;
    Object.assign(_request, requestProps);
    
    if (response.ok) {
        if (response.status === 204) return {}; // TODO: Is it good idea to return an empty object on 204?
        return response.json();
    }
    
    const originalBody = typeof response.text === "function" ? await response.text() : void 0;
    let parsedBody;
    try {
        // When there is no response (empty body) there will be an error thrown
        parsedBody = JSON.parse(originalBody);
    } catch {
        parsedBody = "";
    }
    
    const errorProperties = {
        status: response.status,
        url: original_url,
        tags: {
            response_status: response.status,
            request_id: requestOptions.headers[headers.request_id],
            silent: response.status === 0,
            statusText: response.statusText,
            product: requestOptions.headers[headers.product]
        },
        extra: {
            message: parsedBody,
            url,
            response,
            ui_url: window.location.href,
            request: `${window.location.protocol}//${window.location.host}/merchant/administrator/requests/details/request_id%3A${requestOptions.headers[headers.request_id]}?merchant=gujm`,
            request_id: requestOptions.headers[headers.request_id],
            requestGroupId: requestOptions.headers[headers.groupId],
            duration: requestProps.duration
        }
    };
    
    if (response.status === 504) {
        error = new ApiTimeoutFetchingData(errorProperties);
    } else if (response.status > 299 && ![400, 401, 403, 404, 409].includes(response.status)) {
        // If status is NOT success and NOT in the ignore list
        error = new ApiErrorFetchingData(errorProperties);
    } else if (opts.ensureAuth && response.status === 401 && opts.refresh_token) {
        const refresh_success = await store.dispatch(REFRESH_TOKEN());
    
        // Throw error if refreshing the token is not successful
        if (!refresh_success) {
            emitter.emit("tracking.track", {
                type: "automatic_logout",
                data: {
                    api_decision: false
                }
            });
        
            throw new ErrorNotAuthenticated({
                custom: true,
                message: "Not authenticated",
                hideDetails: true,
                extra: {
                    url
                }
            });
        }
        // Re-run the request if refresh was successful
        return request(original_url, {
            ...opts,
            refresh_token: false // Do not get stuck in the refresh loop
        });
    } else if (response.status === 0) {
        Object.assign(error, errorProperties);
    }
    
    if (error) {
        captureException(error, { captureRequestError: true });
    }
    
    error = error || new RequestError({
        message: parsedBody?.title || originalBody || "Unsuccessful request",
        ...errorProperties
    });
    
    // Add extra props to the error instance
    // TODO: move this step to error constructor
    error.response = response || {};
    error.response.parsed = parsedBody;
    error.response.original = originalBody;
    
    error.request = _request;
    error.requestOptions = {
        url,
        ...requestOptions
    };
    
    throw error;
};

export const makeMarionetteAbortableRequest = function({ view, url, data = {}, ...rest }) {
    if (!view || !view._areRequestEventsMonitored) {
        return request(url, {
            data,
            ...rest
        });
    }
    
    const requestHash = `${url}_${JSON.stringify(data)}`;
    view._requests[requestHash] = new AbortController();
    
    const cleanUp = () => {
        if (view && view._requests) {
            delete view._requests[requestHash];
        }
    };
    
    return request(url, {
        signal: view._requests[requestHash].signal, // Provide only signal to the consumer
        data,
        ...rest
    }).then((result) => {
        cleanUp();
        return result;
    }, (error) => {
        cleanUp();
        // Rethrow the error
        throw error;
    });
};
